import { AnimatePresence, motion } from "motion/react";
import PrimaryButton from "components/common/buttons/PrimaryButton";

interface SelectionFooterProps {
  selectedCount: number;
  /** Recently added product images, newest first. Front card is [0], tinted back card is [1]. */
  thumbnails: string[];
  isEnabled: boolean;
  /** CTA label — swaps to a "go to next collection" prompt while blocks are unfulfilled */
  ctaLabel?: string;
  /** Add-to-cart + discount apply in flight — button shows the dots loader */
  isLoading?: boolean;
  onAddSelected: () => void;
  /** Tapping the thumbnail deck — opens the selected-items view when provided */
  onThumbnailsClick?: () => void;
  /** Selected-items view is open — front card gets the dark active ring */
  isSelectedViewOpen?: boolean;
}

export const SelectionFooter = ({
  selectedCount,
  thumbnails,
  isEnabled,
  ctaLabel = "Add Selected",
  isLoading = false,
  onAddSelected,
  onThumbnailsClick,
  isSelectedViewOpen = false,
}: SelectionFooterProps) => (
  <div
    className="bg-white flex gap-2 items-start px-4 pb-4 w-full"
    style={{
      boxShadow:
        "0px -9px 4.5px white, 0px -21px 6px rgba(255, 255, 255, 0.5), 0px -37px 7.5px rgba(255, 255, 255, 0.15)",
    }}>
    {selectedCount > 0 && thumbnails.length > 0 && (
      <div
        onClick={onThumbnailsClick}
        className={`flex items-center justify-center relative shrink-0 w-16 self-stretch ${
          onThumbnailsClick ? "cursor-pointer" : ""
        }`}>
        <div className="h-full relative shrink-0 w-[52px]">
          {/* Card deck: newest image drops in from above as the front card, the previous front
                tilts right and shrinks into a tinted back card. Anything older fades away. */}
          <AnimatePresence initial={false}>
            {thumbnails.slice(0, 2).map((src, index) => (
                <motion.div
                  key={src}
                  initial={{ y: -32, opacity: 0 }}
                  animate={
                  index === 0
                      ? { y: 0, x: 0, opacity: 1, scale: 1, rotate: 0, zIndex: 2 }
                      : { y: 0, x: 14, opacity: 0.6, scale: 0.85, rotate: 10, zIndex: 1 }
                  }
                  exit={{ opacity: 0, zIndex: 0 }}
                  transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                className="absolute left-0 rounded-xl size-[52px] top-0 bg-black/[0.06]">
                  {/* Front card gets the dark active ring only while the selected-items view is open */}
                  <div
                    className="absolute inset-0 size-full object-cover rounded-xl z-1"
                    style={{
                      boxShadow:
                        index === 0 && isSelectedViewOpen
                          ? "0 0 0 2px #27272A, 0 0 0 3px #FFF inset"
                          : "inset 0 0 0 2px rgba(0, 0, 0, 0.08)",
                    }}></div>

                  <img src={src} alt="" className="size-full object-cover rounded-xl" />

                </motion.div>
            ))}
          </AnimatePresence>
          {/* Badge scales up when a product is added and settles back once the card animation ends */}
          <motion.div
            key={selectedCount}
            animate={{ scale: [1, 1.3, 1.3, 1] }}
            transition={{ duration: 0.55, times: [0, 0.15, 0.75, 1], ease: "easeOut" }}
            className="absolute bg-primary-dark flex items-center justify-center min-w-[20px] px-1 py-0 pt-0 right-[-5px] rounded-full top-[-8px] z-10">
            <span className="text-text-xs-bold text-center text-btnPrimaryText">{selectedCount}</span>
          </motion.div>
        </div>
      </div>
    )}
    <PrimaryButton
      buttonText={ctaLabel}
      height="h-[52px]"
      isDisabled={!isEnabled}
      isLoading={isLoading}
      onClick={onAddSelected}
      containerClassName="flex-1 !w-auto min-w-0"
      className="text-text-md-semibold"
    />
  </div>
);
